import React, { useState, useEffect } from 'react';
import DashboardPage from './components/DashboardPage';
import AddProductForm from './components/AddProductForm';
import StockList from './components/StockList';
import BatteryStockList from './components/BatteryStockList';
import ChargerStockList from './components/ChargerStockList';
import CustomerPage from './components/CustomerPage';
import QuotationPage from './components/QuotationPage';
import SalePage from './components/SalePage';
import InspectionPage from './components/InspectionPage';
import PublicInspectionView from './components/PublicInspectionView';
import OrderPage from './components/OrderPage';
import UserPage from './components/UserPage';
import LoginPage from './components/LoginPage';
import CalendarPage from './components/CalendarPage';
import { StockProvider } from './contexts/StockContext';

import 'bootstrap/dist/css/bootstrap.min.css';
import { ReactComponent as BatteryLogo } from './logo-battery.svg';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showNavDropdown, setShowNavDropdown] = useState(false);
  const tabLabels = {
    dashboard: 'แดชบอร์ด',
    customer: 'ลูกค้า',
    quotation: 'เสนอราคา',
    sale: 'ขาย',
    inspection: 'ตรวจสภาพ',
    order: 'ออเดอร์',
    addProduct: 'เพิ่มสินค้า',
    batteryStock: 'สต๊อกแบต',
    chargerStock: 'สต๊อกชาร์จเจอร์',
    user: 'จัดการผู้ใช้',
  };

  // ตรวจสอบว่าเป็นหน้าสาธารณะหรือไม่
  const isPublicRoute = window.location.pathname.startsWith('/public/serial/');

  useEffect(() => {
    // ตรวจสอบ session ที่เก็บไว้ใน localStorage
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('currentUser');
      }
    }

    // ตั้งค่าแท็บเริ่มต้นตาม URL (รองรับเฉพาะหน้า stock)
    const path = window.location.pathname;
    if (path === '/battery-stock') {
      setActiveTab('batteryStock');
    } else if (path === '/charger-stock') {
      setActiveTab('chargerStock');
    }
    setIsLoading(false);
  }, []);

  // อัปเดต URL เมื่อมีการเปลี่ยนแท็บ (เฉพาะแท็บที่ต้องการแชร์ลิงก์)
  // ป้องกันการทับเส้นทางสาธารณะ เช่น /public/serial/:serial
  useEffect(() => {
    if (isPublicRoute) return; // อย่าเปลี่ยน URL เมื่ออยู่บนหน้าสาธารณะ

    const tabToPath = {
      dashboard: '/',
      batteryStock: '/battery-stock',
      chargerStock: '/charger-stock'
    };
    const nextPath = tabToPath[activeTab];
    if (nextPath && window.location.pathname !== nextPath) {
      window.history.pushState(null, '', nextPath);
    }
  }, [activeTab, isPublicRoute]);

  // ตรวจจับขนาดหน้าจอเพื่อสลับแสดงเมนูเป็น dropdown บนจอแคบ
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowNavDropdown(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    // เก็บข้อมูลผู้ใช้ใน localStorage
    localStorage.setItem('currentUser', JSON.stringify(user));
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setActiveTab('dashboard');
    // ลบข้อมูลผู้ใช้จาก localStorage
    localStorage.removeItem('currentUser');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 thai-text">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  // แสดงหน้าสาธารณะโดยไม่ต้อง login
  if (isPublicRoute) {
    const serialNumber = window.location.pathname.split('/').pop();
    return <PublicInspectionView serialNumber={serialNumber} />;
  }

  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const renderContent = () => {
    console.log('=== App renderContent called, activeTab:', activeTab);

    // Define permissions
    const rolePermissions = {
      admin: ['dashboard', 'customer', 'quotation', 'sale', 'inspection', 'order', 'addProduct', 'batteryStock', 'chargerStock', 'user', 'calendar'],
      stock: ['dashboard', 'stock', 'batteryStock', 'chargerStock', 'addProduct', 'customer', 'inspection', 'order'],
      sale: ['dashboard', 'customer', 'quotation', 'sale', 'order', 'calendar'],
      service: ['customer', 'inspection'], // Service role only sees Customer and Inspection
      user: ['dashboard', 'customer', 'quotation', 'sale', 'inspection', 'order', 'addProduct', 'batteryStock', 'chargerStock', 'calendar'] // User sees everything except User Management
    };

    // Check if user has permission
    const userRole = currentUser?.role || 'user';
    const allowedTabs = rolePermissions[userRole] || rolePermissions['user'];

    // Special case for 'stock' tab which maps to StockList component but might not be in the list explicitly if using granular stock tabs
    // But here we use 'batteryStock' and 'chargerStock'

    if (!allowedTabs.includes(activeTab) && activeTab !== 'dashboard') {
      // Allow dashboard as fallback for most, but service might not have it.
      // If service tries to access dashboard (default), redirect to customer
      if (userRole === 'service' && activeTab === 'dashboard') {
        setTimeout(() => setActiveTab('customer'), 0);
        return null;
      }

      return (
        <div className="alert alert-danger m-4" role="alert">
          <h4 className="alert-heading">ไม่มีสิทธิ์เข้าถึง</h4>
          <p>คุณไม่มีสิทธิ์เข้าถึงหน้านี้ ({activeTab})</p>
          <hr />
          <p className="mb-0">กรุณาติดต่อผู้ดูแลระบบหากคุณต้องการสิทธิ์การใช้งาน</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'addProduct':
        return <AddProductForm />;
      case 'stock':
        return <StockList />;
      case 'batteryStock':
        return <BatteryStockList currentUser={currentUser} onNavigate={(tab) => setActiveTab(tab)} />;
      case 'chargerStock':
        return <ChargerStockList currentUser={currentUser} onNavigate={(tab) => setActiveTab(tab)} />;
      case 'sale':
        return <SalePage currentUser={currentUser} />;
      case 'quotation':
        console.log('=== Rendering QuotationPage ===');
        return <QuotationPage currentUser={currentUser} />;
      case 'inspection':
        return <InspectionPage currentUser={currentUser} />;
      case 'customer':
        return <CustomerPage />;
      case 'order':
        return <OrderPage />;
      case 'user':
        return <UserPage onLogout={handleLogout} currentUser={currentUser} />;
      case 'calendar':
        return <CalendarPage currentUser={currentUser} />;
      case 'dashboard':
      default:
        // Double check for service role on default/dashboard
        if (userRole === 'service') {
          return <CustomerPage />; // Fallback for service is CustomerPage
        }
        return <DashboardPage onNavigate={(tab) => setActiveTab(tab)} />;
    }
  };

  return (
    <div>
      {/* Header with Logo */}
      <div className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="left-logo">
              <img src="/images/ktpan logo-white.png" alt="KTPAN Logo" className="company-logo" />
            </div>
            <div className="app-logo">
              <BatteryLogo />
              <span className="thai-text">ระบบจัดการแบตเตอรี่</span>
            </div>
            <div className="user-info">
              <div className="dropdown">
                <button
                  className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                  type="button"
                  data-bs-toggle="dropdown"
                >
                  <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center me-2">
                    <i className="fas fa-user text-primary"></i>
                  </div>
                  <div className="d-flex flex-column align-items-start">
                    <span className="thai-text fw-bold">{currentUser.name}</span>
                    <small className="text-light opacity-75">{currentUser.position || 'ผู้ใช้งาน'}</small>
                  </div>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <span className="dropdown-item-text">
                      <small className="text-muted thai-text">
                        สิทธิ์: {currentUser.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ผู้ใช้งานทั่วไป'}
                      </small>
                    </span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2"></i>
                      <span className="thai-text">ออกจากระบบ</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {isMobile ? (
          <div className="nav-mobile position-relative my-2">
            <button
              className="btn btn-outline-secondary nav-mobile-toggle"
              onClick={() => setShowNavDropdown(!showNavDropdown)}
              aria-expanded={showNavDropdown}
              title={tabLabels[activeTab] || 'เมนู'}
            >
              <i className="fas fa-bars me-2"></i>
              <span className="thai-text nav-mobile-current">{tabLabels[activeTab] || 'เมนู'}</span>
            </button>
            {showNavDropdown && (
              <div className="nav-mobile-menu dropdown-menu show">
                <button className={`dropdown-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => { setActiveTab('dashboard'); setShowNavDropdown(false); }}>
                  <i className="fas fa-chart-line me-2"></i>
                  <span className="thai-text">แดชบอร์ด</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'customer' ? 'active' : ''}`} onClick={() => { setActiveTab('customer'); setShowNavDropdown(false); }}>
                  <i className="fas fa-users me-2"></i>
                  <span className="thai-text">ลูกค้า</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'quotation' ? 'active' : ''}`} onClick={() => { console.log('=== Quotation menu clicked ==='); setActiveTab('quotation'); setShowNavDropdown(false); }}>
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  <span className="thai-text">เสนอราคา</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'sale' ? 'active' : ''}`} onClick={() => { setActiveTab('sale'); setShowNavDropdown(false); }}>
                  <i className="fas fa-shopping-cart me-2"></i>
                  <span className="thai-text">ขาย</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'inspection' ? 'active' : ''}`} onClick={() => { setActiveTab('inspection'); setShowNavDropdown(false); }}>
                  <i className="fas fa-search me-2"></i>
                  <span className="thai-text">ตรวจสภาพ</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'order' ? 'active' : ''}`} onClick={() => { setActiveTab('order'); setShowNavDropdown(false); }}>
                  <i className="fas fa-clipboard-list me-2"></i>
                  <span className="thai-text">ออเดอร์</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'addProduct' ? 'active' : ''}`} onClick={() => { setActiveTab('addProduct'); setShowNavDropdown(false); }}>
                  <i className="fas fa-plus-circle me-2"></i>
                  <span className="thai-text">เพิ่มสินค้า</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'batteryStock' ? 'active' : ''}`} onClick={() => { setActiveTab('batteryStock'); setShowNavDropdown(false); }}>
                  <i className="fas fa-battery-full me-2"></i>
                  <span className="thai-text">สต๊อกแบต</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'chargerStock' ? 'active' : ''}`} onClick={() => { setActiveTab('chargerStock'); setShowNavDropdown(false); }}>
                  <i className="fas fa-plug me-2"></i>
                  <span className="thai-text">สต๊อกชาร์จเจอร์</span>
                </button>
                <button className={`dropdown-item ${activeTab === 'user' ? 'active' : ''}`} onClick={() => {
                  if (currentUser.role === 'admin') {
                    setActiveTab('user');
                  } else {
                    alert('คุณไม่มีสิทธิ์เข้าถึงเมนูนี้ เฉพาะผู้ดูแลระบบเท่านั้น');
                  }
                  setShowNavDropdown(false);
                }}>
                  <i className="fas fa-user-cog me-2"></i>
                  <span className="thai-text">จัดการผู้ใช้</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="nav nav-tabs">
            {/* 1. แดชบอร์ด */}
            {(currentUser?.role !== 'service') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                  onClick={() => setActiveTab('dashboard')}
                >
                  <i className="fas fa-chart-line me-2"></i>
                  <span className="thai-text">แดชบอร์ด</span>
                </button>
              </li>
            )}
            {/* 2. ลูกค้า */}
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'customer' ? 'active' : ''}`}
                onClick={() => setActiveTab('customer')}
              >
                <i className="fas fa-users me-2"></i>
                <span className="thai-text">ลูกค้า</span>
              </button>
            </li>
            {/* 3. เสนอราคา */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'sale' || currentUser?.role === 'user') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'quotation' ? 'active' : ''}`}
                  onClick={() => {
                    console.log('=== Quotation menu clicked ===');
                    setActiveTab('quotation');
                  }}
                >
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  <span className="thai-text">เสนอราคา</span>
                </button>
              </li>
            )}
            {/* 4. ขาย */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'sale' || currentUser?.role === 'user') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'sale' ? 'active' : ''}`}
                  onClick={() => setActiveTab('sale')}
                >
                  <i className="fas fa-shopping-cart me-2"></i>
                  <span className="thai-text">ขาย</span>
                </button>
              </li>
            )}
            {/* 5. ตรวจสภาพ */}
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'inspection' ? 'active' : ''}`}
                onClick={() => setActiveTab('inspection')}
              >
                <i className="fas fa-search me-2"></i>
                <span className="thai-text">ตรวจสภาพ</span>
              </button>
            </li>
            {/* 6. ออเดอร์ */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'sale' || currentUser?.role === 'user' || currentUser?.role === 'stock') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'order' ? 'active' : ''}`}
                  onClick={() => setActiveTab('order')}
                >
                  <i className="fas fa-clipboard-list me-2"></i>
                  <span className="thai-text">ออเดอร์</span>
                </button>
              </li>
            )}
            {/* 7. เพิ่มสินค้า */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'stock' || currentUser?.role === 'user') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'addProduct' ? 'active' : ''}`}
                  onClick={() => setActiveTab('addProduct')}
                >
                  <i className="fas fa-plus-circle me-2"></i>
                  <span className="thai-text">เพิ่มสินค้า</span>
                </button>
              </li>
            )}
            {/* 8. สต๊อกแบต */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'stock' || currentUser?.role === 'user') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'batteryStock' ? 'active' : ''}`}
                  onClick={() => setActiveTab('batteryStock')}
                >
                  <i className="fas fa-battery-full me-2"></i>
                  <span className="thai-text">สต๊อกแบต</span>
                </button>
              </li>
            )}
            {/* 9. สต๊อกชาร์จเจอร์ */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'stock' || currentUser?.role === 'user') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'chargerStock' ? 'active' : ''}`}
                  onClick={() => setActiveTab('chargerStock')}
                >
                  <i className="fas fa-plug me-2"></i>
                  <span className="thai-text">สต๊อกชาร์จเจอร์</span>
                </button>
              </li>
            )}

            {/* 11. จัดการผู้ใช้ */}
            {(currentUser?.role === 'admin') && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'user' ? 'active' : ''}`}
                  onClick={() => setActiveTab('user')}
                >
                  <i className="fas fa-user-cog me-2"></i>
                  <span className="thai-text">จัดการผู้ใช้</span>
                </button>
              </li>
            )}
          </ul>
        )}

        <div className="mt-4">
          <StockProvider>
            {renderContent()}
          </StockProvider>
        </div>
      </div>
    </div>
  );
}

export default App;
